<script setup lang="ts">
import type { DateValue } from '@internationalized/date'
import { getLocalTimeZone, today, parseDate } from '@internationalized/date'
import { Calendar } from '~/components/ui/calendar'

const route = useRoute()
const router = useRouter()
const { getPathWithLocale } = useLocalizedPath()

const getInitialDate = () => {
  const { year, month, day } = route.params as { year?: string; month?: string; day?: string }

  if (year && month && day) {
    try {
      const dateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      return parseDate(dateString)
    } catch (error) {
      console.error('Invalid date from params:', { year, month, day })
      console.error(error)
      return today(getLocalTimeZone())
    }
  }

  return today(getLocalTimeZone())
}

const selectedDate = ref(getInitialDate()) as Ref<DateValue>
const placeholder = ref(selectedDate.value)

const displayedMonth = ref(placeholder.value.month)
const displayedYear = ref(placeholder.value.year)

const availableDatesData = ref<{ dates: number[] } | null>(null)

const loadAvailableDates = async (month: number, year: number) => {
  // Clear previous month's dates so stale dates are not shown while loading
  availableDatesData.value = { dates: [] }
  availableDatesData.value = await useApiCall<{ dates: number[] }>(
    `/news/dates?month=${month}&year=${year}`
  )
}

await loadAvailableDates(displayedMonth.value, displayedYear.value)

const onPlaceholderUpdate = async (newPlaceholder: DateValue) => {
  placeholder.value = newPlaceholder
  displayedMonth.value = newPlaceholder.month
  displayedYear.value = newPlaceholder.year
  await loadAvailableDates(newPlaceholder.month, newPlaceholder.year)
}

const availableDates = computed(() => {
  const dates = availableDatesData.value?.dates || []
  return dates.map((day) => {
    const formattedMonth = displayedMonth.value.toString().padStart(2, '0')
    const formattedDay = day.toString().padStart(2, '0')
    return `${displayedYear.value}-${formattedMonth}-${formattedDay}`
  })
})

const isDateUnavailable = (date: DateValue) => {
  const dateString = date.toString()
  return !availableDates.value.includes(dateString)
}

watch(
  selectedDate,
  (newValue: DateValue) => {
    if (!newValue) return
    const dateString = newValue.toString()

    const [year, month, day] = dateString.split('-')
    const formattedMonth = month.padStart(2, '0')
    const formattedDay = day.padStart(2, '0')
    const newPath = `/news/${year}/${formattedMonth}/${formattedDay}`

    router.push(getPathWithLocale(newPath))
  }
)
</script>

<template>
  <Calendar
    v-model="selectedDate"
    :weekday-format="'short'"
    :week-starts-on="1"
    :locale="'ru'"
    :is-date-unavailable="isDateUnavailable"
    :placeholder="placeholder"
    @update:placeholder="onPlaceholderUpdate"
  />
</template>
